import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useOpenTarget } from "@/lib/deeplink";
import { PageHeader, Surface, Empty } from "@/components/app/Primitives";
import PushToggle from "@/components/app/PushToggle";
import { Bell, CheckCheck, FileText, CalendarCheck, Gift, FileSignature, Users } from "lucide-react";

const KIND_ICON = (k) => /leave|overtime|attendance/.test(k) ? CalendarCheck
    : /document/.test(k) ? FileText
    : /contract/.test(k) ? FileSignature
    : /hire/.test(k) ? Users
    : /reward|redeem/.test(k) ? Gift : Bell;

export default function Notifications() {
    const navigate = useNavigate();
    const open = useOpenTarget();
    const { companies } = useAuth();
    const nameOf = (id) => (companies || []).find((c) => c.id === id)?.name;
    const [data, setData] = useState(null);
    const load = () => api.get("/me/notifications").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const readAll = async () => { await api.post("/me/notifications/read-all"); load(); };

    return (
        <div>
            <PageHeader title="Notifications" subtitle="Approvals, document releases, contract alerts, and HR updates — with email copies when mail is configured."
                actions={data?.unread > 0 && (
                    <button className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-2" onClick={readAll} data-testid="read-all-btn">
                        <CheckCheck className="size-4" /> Mark all read ({data.unread})
                    </button>
                )} />

            <div className="mb-3"><PushToggle /></div>
            {data && !data.items.length && <Surface className="p-6"><Empty title="No notifications yet" subtitle="Decisions on your requests will appear here." /></Surface>}
            <div className="space-y-2 max-w-3xl">
                {(data?.items || []).map((n) => {
                    const Icon = KIND_ICON(n.kind || "");
                    return (
                        <Surface key={n.id} className={`p-3 flex items-start gap-3 ${n.read ? "opacity-70" : "border-primary/40"} ${n.link ? "cursor-pointer hover:shadow-sm" : ""}`}
                            onClick={async () => {
                                if (!n.read) { try { await api.post(`/me/notifications/${n.id}/read`); } catch { /* still open it */ } }
                                if (n.link) open(n.link, n.company_id); else load();
                            }} data-testid={`notif-${n.id}`}>
                            <div className={`size-9 rounded-lg flex items-center justify-center shrink-0 ${n.read ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                                <Icon className="size-4" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-medium">{n.title}{!n.read && <span className="ml-2 inline-block size-2 rounded-full bg-primary" />}</div>
                                {n.body && <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>}
                                <div className="text-[10px] text-muted-foreground mt-1">{formatDateTime(n.created_at)}{(companies || []).length > 1 && nameOf(n.company_id) ? ` · ${nameOf(n.company_id)}` : ""}{n.link ? " · opens the item" : ""}</div>
                            </div>
                        </Surface>
                    );
                })}
            </div>
        </div>
    );
}
